import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { FilePlus2, Pencil } from "lucide-react";

import { medicalService, type ClinicLearner, type Immunization, type MedicalRecord, type MedicalVisit } from "../../services/medicalService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ClinicalAlerts, LearnerPick, orNothing } from "./ClinicParts";
import { BLOOD_TYPES, VACCINE_TYPES, VISIT_TYPES, clock, day, labelFrom, recordConfig, recordFlags, sortLearners, visitStatus } from "./medicalLogic";

const TEXT_FIELDS = [
  "allergies", "chronic_conditions", "medications", "previous_surgeries", "family_medical_history",
  "dietary_restrictions", "physical_limitations", "special_instructions", "immunization_notes",
  "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone", "emergency_contact_address",
  "insurance_provider", "insurance_policy_number",
] as const;
const OPTIONAL_VALUES = ["blood_type", "height_cm", "weight_kg", "last_immunization_date", "insurance_expiry"] as const;

/** A learner's standing health record: what to watch for, who to call, and the body measurements. */
function RecordForm({ record, learners, onClose, onDone }: {
  record?: MedicalRecord;
  /** Learners who have no record yet; used when creating. */
  learners: ClinicLearner[];
  onClose: () => void;
  onDone: (record: MedicalRecord) => void;
}) {
  const [learner, setLearner] = useState("");
  const [immunised, setImmunised] = useState(record?.immunization_complete ?? false);
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      record ? medicalService.updateRecord({ id: record.id, data }) : medicalService.createRecord({ ...data, student_id: Number(learner) }),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const data: Record<string, unknown> = { immunization_complete: immunised };
    for (const name of TEXT_FIELDS) data[name] = String(form.get(name) ?? "").trim();
    for (const name of OPTIONAL_VALUES) data[name] = String(form.get(name) ?? "").trim() || null;
    save.mutate(data);
  };
  const value = (name: keyof MedicalRecord) => (record?.[name] as string | null | undefined) ?? "";
  const area = (name: (typeof TEXT_FIELDS)[number], label: string, hint?: string, wide = false) => (
    <Field label={label} optional hint={hint} className={wide ? "sm:col-span-2" : undefined}>
      <textarea name={name} rows={2} defaultValue={value(name)} className="input mt-1" />
    </Field>
  );

  return (
    <FormModal
      id="health-record"
      title={record ? `Edit health record · ${record.student_name}` : "New health record"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The health record could not be saved.") : undefined}
      saveLabel={record ? "Save changes" : "Create the record"}
      note="Confidential: only clinical staff and school leadership can read it."
      onClose={onClose}
      onSubmit={submit}
    >
      {!record && (
        <FormSection title="The learner" divided={false}>
          <LearnerPick learners={learners} value={learner} onChange={setLearner} empty="Every learner already has a record" />
        </FormSection>
      )}

      <FormSection title="What to watch for" description="Shown in red and amber wherever the learner is treated or prescribed for." divided={!record}>
        {area("allergies", "Allergies", "Medicines, foods, insect stings… Write “none known” if checked.", true)}
        {area("chronic_conditions", "Long-term conditions", "e.g. asthma, sickle cell, epilepsy, diabetes.")}
        {area("medications", "Regular medicines", "Taken at home or in school.")}
        {area("physical_limitations", "Physical limitations", "e.g. no contact sport.")}
        {area("dietary_restrictions", "Diet", "e.g. no pork, lactose free.")}
        {area("special_instructions", "What to do in an emergency", "e.g. inhaler in the class teacher's drawer.", true)}
      </FormSection>

      <FormSection title="Measurements" columns={3}>
        <Field label="Blood group" optional>
          <select name="blood_type" defaultValue={value("blood_type")} className="input mt-1">
            <option value="">Not known</option>
            {BLOOD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </Field>
        <Field label="Height (cm)" optional>
          <input type="number" name="height_cm" min={30} max={250} step="0.1" defaultValue={value("height_cm")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Weight (kg)" optional hint="BMI is worked out from these two.">
          <input type="number" name="weight_kg" min={2} max={250} step="0.1" defaultValue={value("weight_kg")} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="Emergency contact">
        <Field label="Name" optional>
          <input name="emergency_contact_name" defaultValue={value("emergency_contact_name")} className="input mt-1" />
        </Field>
        <Field label="Relationship" optional>
          <input name="emergency_contact_relationship" list="relationships" defaultValue={value("emergency_contact_relationship")} className="input mt-1" />
          <datalist id="relationships">{["Mother", "Father", "Guardian", "Aunt", "Uncle", "Grandparent", "Sibling"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="emergency_contact_phone" defaultValue={value("emergency_contact_phone")} className="input mt-1" placeholder="e.g. 0772 123456" />
        </Field>
        <Field label="Address" optional>
          <input name="emergency_contact_address" defaultValue={value("emergency_contact_address")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="History" description="Background a clinician may need.">
        {area("previous_surgeries", "Operations and hospital stays")}
        {area("family_medical_history", "Family history")}
      </FormSection>

      <FormSection title="Immunisation">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={immunised} onChange={(event) => setImmunised(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Childhood immunisations are complete (seen on the child health card)
        </label>
        <Field label="Last immunisation" optional>
          <input type="date" name="last_immunization_date" max={todayIso()} defaultValue={value("last_immunization_date")} className="input mt-1" />
        </Field>
        {area("immunization_notes", "Immunisation notes")}
      </FormSection>

      <FormSection title="Medical insurance" columns={3}>
        <Field label="Provider" optional>
          <input name="insurance_provider" defaultValue={value("insurance_provider")} className="input mt-1" />
        </Field>
        <Field label="Policy number" optional>
          <input name="insurance_policy_number" defaultValue={value("insurance_policy_number")} className="input mt-1" />
        </Field>
        <Field label="Cover ends" optional>
          <input type="date" name="insurance_expiry" defaultValue={value("insurance_expiry")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** One health record per learner, and the learner's sick-bay history beside it. */
export function RecordsPanel({ records, learners, visits, immunisations, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "follow-up" from the dashboard. */
  initialView?: string;
  records?: MedicalRecord[];
  learners: ClinicLearner[];
  visits: MedicalVisit[];
  immunisations: Immunization[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<MedicalRecord | "new" | null>(null);
  const classOf = new Map(learners.map((learner) => [learner.id, learner.class_name ?? ""]));
  const withRecord = new Set((records ?? []).map((row) => row.student));
  const without = sortLearners(learners.filter((learner) => !withRecord.has(learner.id)));

  const columns: RegisterColumn<MedicalRecord>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Class", cell: (row) => classOf.get(row.student) || "—", exportValue: (row) => classOf.get(row.student) ?? "", className: "text-gray-700" },
    { header: "Age", cell: (row) => row.age ?? "—", exportValue: (row) => row.age ?? "", align: "right" },
    { header: "Blood", cell: (row) => row.blood_type || "—", exportValue: (row) => row.blood_type ?? "", className: "text-gray-700" },
    {
      header: "Watch for",
      cell: (row) => {
        const flags = recordFlags(row);
        return flags.length ? (
          <span className="flex flex-wrap gap-1">
            {flags.map((flag) => (
              <span key={flag} className={`rounded px-1.5 py-0.5 text-xs font-medium ${flag === "Allergies" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{flag}</span>
            ))}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
      exportValue: (row) => recordFlags(row).join(", "),
    },
    { header: "Allergies", cell: (row) => row.allergies ?? "", exportValue: (row) => row.allergies ?? "", exportOnly: true },
    { header: "Long-term conditions", cell: (row) => row.chronic_conditions ?? "", exportValue: (row) => row.chronic_conditions ?? "", exportOnly: true },
    {
      header: "Emergency contact",
      cell: (row) => (row.emergency_contact_phone ? `${row.emergency_contact_name || "Contact"} · ${row.emergency_contact_phone}` : <span className="text-gray-400">None</span>),
      exportValue: (row) => [row.emergency_contact_name, row.emergency_contact_phone].filter(Boolean).join(" "),
      className: "text-gray-700",
    },
    {
      header: "Immunisation",
      cell: (row) => <StatusDot label={row.immunization_complete ? "Complete" : "Incomplete"} dot={row.immunization_complete ? "bg-emerald-500" : "bg-amber-500"} />,
      exportValue: (row) => (row.immunization_complete ? "Complete" : "Incomplete"),
    },
  ];

  const record = viewing !== null ? records?.find((row) => row.id === viewing) : undefined;
  const history = record ? visits.filter((row) => row.student === record.student) : [];
  const shots = record ? immunisations.filter((row) => row.student === record.student) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {canManage && !isLoading && learners.length > 0 && without.length > 0 && (
        <p className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-gray-700">
          {without.length} of {learners.length} learners in school have no health record yet, so their allergies are unknown at the sick bay.
        </p>
      )}
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Health records"
        description="Open a record for everything on it and the learner's visits and immunisations."
        actions={canManage && (
          <button type="button" disabled={!without.length} onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> New health record
          </button>
        )}
        rows={records}
        config={recordConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["health record", "health records"]}
        exportName={{ file: "health-records", title: "Learner Health Records" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No health records yet. Start with learners who have allergies or long-term conditions." : "No health records yet."}
      />

      {record && !editing && (
        <DetailsModal
          size="3xl"
          title={`${record.student_name} · health record`}
          headline={{ label: "Blood group", value: record.blood_type || "Not known", note: [classOf.get(record.student), record.age != null && `aged ${record.age}`].filter(Boolean).join(" · ") || undefined }}
          status={{ label: record.immunization_complete ? "Immunisation complete" : "Immunisation incomplete", dot: record.immunization_complete ? "bg-emerald-500" : "bg-amber-500" }}
          notice={<ClinicalAlerts record={record} />}
          facts={[
            ["Learner number", record.student_id],
            ["Height", record.height_cm ? `${Number(record.height_cm)} cm` : undefined],
            ["Weight", record.weight_kg ? `${Number(record.weight_kg)} kg` : undefined],
            ["BMI", record.bmi ? String(Number(record.bmi)) : undefined],
            ["Emergency contact", orNothing(record.emergency_contact_name, record.emergency_contact_relationship && `(${record.emergency_contact_relationship})`)],
            ["Contact phone", record.emergency_contact_phone ? <a key="phone" href={`tel:${record.emergency_contact_phone}`} className="text-primary-700 hover:underline">{record.emergency_contact_phone}</a> : undefined],
            ["Last immunisation", day(record.last_immunization_date) || undefined],
            ["Insurance", orNothing(record.insurance_provider, record.insurance_policy_number && `· ${record.insurance_policy_number}`)],
            ["Cover ends", record.insurance_expiry ? <span key="cover" className={record.insurance_expiry < today ? "text-red-700" : undefined}>{day(record.insurance_expiry)}</span> : undefined],
            ["Last updated", record.last_updated ? day(record.last_updated) : undefined],
          ]}
          sections={[
            { title: "Diet", body: record.dietary_restrictions },
            { title: "What to do in an emergency", body: record.special_instructions, tone: "warning" },
            { title: "Operations and hospital stays", body: record.previous_surgeries },
            { title: "Family history", body: record.family_medical_history },
            { title: "Immunisation notes", body: record.immunization_notes },
            { title: "Contact address", body: record.emergency_contact_address },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(record)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sick-bay visits ({history.length})</h4>
            {history.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {history.slice(0, 8).map((row) => (
                  <li key={row.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="tabular-nums text-gray-500">{day(row.visit_date)} {clock(row.visit_time)}</span>
                      <span className="ml-2 font-medium text-gray-900">{labelFrom(VISIT_TYPES, row.visit_type)}</span>
                      <span className="block text-gray-700">{row.diagnosis || row.chief_complaint}</span>
                    </span>
                    <StatusDot {...visitStatus(row, today)} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">Not seen at the sick bay yet.</p>
            )}
          </section>
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Immunisations given at school ({shots.length})</h4>
            {shots.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {shots.map((row) => (
                  <li key={row.id} className="flex flex-wrap justify-between gap-2 px-3 py-2 text-sm">
                    <span>
                      <span className="font-medium text-gray-900">{row.vaccine_name}</span>
                      <span className="text-gray-600"> · {labelFrom(VACCINE_TYPES, row.vaccine_type)}</span>
                    </span>
                    <span className="tabular-nums text-gray-600">{day(row.vaccination_date)}{row.next_dose_due ? ` · next ${day(row.next_dose_due)}` : ""}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">None recorded.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <RecordForm
          record={editing === "new" ? undefined : editing}
          learners={without}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `A health record was created for ${saved.student_name}.` : `${saved.student_name}'s health record was updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
